import React, { useCallback, useContext, useMemo } from 'react';
import SlideOutContext from '../contexts/PerformanceBoardSlideOutContext';
import SlideOutHeader from './SlideOutHeader';
import PerformanceSlideoutWizard from './Wizards/PerformanceSlideoutWizard';
import sectionTypeCheckers from './sectionTypeCheckers';
import MetricSlideoutWizard from './Wizards/MetricSlideoutWizard';
import GridSlideoutWizard from './Wizards/GridSlideoutWizard';
import PropertiesSlideoutWizard from './Wizards/PropertiesSlideoutWizard';
import TargetProgressSlideoutWizard from './Wizards/TargetProgressSlideoutWizard';
import RelatedDocumentsSlideoutWizard from './Wizards/RelatedDocumentsSlideoutWizard';
import RelatedMetricDocumentsSlideoutWizard from './Wizards/RelatedMetricDocumentsSlideoutWizard';
import AllMetricsGate from 'components/AllMetricsGate';
import BonusTotalRewardWizard from './Wizards/BonusTotalRewardWizard';
import BonusRewardWizard from './Wizards/BonusRewardWizard';
import BonusEligibilityWizard from './Wizards/BonusEligibilityWizard';
import RecognitionPaymentsWizard from './Wizards/RecognitionPaymentsWizard';

const WizardSlideout = () => {
  const { navState, setNavState, board } = useContext(SlideOutContext);

  const lockedBonusConfigId = useMemo((): string | undefined => {
    const bonusSection = board.slideout.find(
      (s) =>
        sectionTypeCheckers.isBonusRewardSection(s) ||
        sectionTypeCheckers.isBonusTotalRewardSection(s) ||
        sectionTypeCheckers.isBonusEligibilitySection(s) ||
        sectionTypeCheckers.isRecognitionPaymentsSection(s),
    );
    if (!bonusSection) {
      return undefined;
    }
    return (
      bonusSection as
        | PerformanceBoardTypes.SlideOut.BonusRewardSection
        | PerformanceBoardTypes.SlideOut.BonusTotalRewardSection
        | PerformanceBoardTypes.SlideOut.BonusEligibilitySection
        | PerformanceBoardTypes.SlideOut.RecognitionPaymentsSection
    ).bonusConfigId;
  }, [board.slideout]);

  const close = useCallback(() => {
    if (board.slideout.length === 0) {
      setNavState({ slideoutMode: 'Empty' });
    } else {
      setNavState({ slideoutMode: 'Home' });
    }
  }, [board.slideout.length, setNavState]);

  return (
    <>
      <SlideOutHeader
        slideoutMode="Creating"
        type={navState.creatingSectionType}
      />
      {navState.creatingSectionType === 'Performance Graph' && (
        <PerformanceSlideoutWizard
          close={close}
          editingSection={
            navState.editingSection &&
            sectionTypeCheckers.isPerformanceSection(navState.editingSection)
              ? navState.editingSection
              : undefined
          }
        />
      )}
      {navState.creatingSectionType === 'Metric Widget' && (
        <MetricSlideoutWizard
          close={close}
          editingSection={
            navState.editingSection &&
            sectionTypeCheckers.isMetricSection(navState.editingSection)
              ? navState.editingSection
              : undefined
          }
        />
      )}
      {navState.creatingSectionType === 'Simple Grid' && (
        <GridSlideoutWizard
          close={close}
          editingSection={
            navState.editingSection &&
            sectionTypeCheckers.isGridSection(navState.editingSection)
              ? navState.editingSection
              : undefined
          }
        />
      )}
      {navState.creatingSectionType === 'Properties' && (
        <PropertiesSlideoutWizard
          close={close}
          editingSection={
            navState.editingSection &&
            sectionTypeCheckers.isPropertiesSection(navState.editingSection)
              ? navState.editingSection
              : undefined
          }
        />
      )}
      {navState.creatingSectionType === 'Target Progress' && (
        <TargetProgressSlideoutWizard
          close={close}
          editingSection={
            navState.editingSection &&
            sectionTypeCheckers.isTargetProgressSection(navState.editingSection)
              ? navState.editingSection
              : undefined
          }
        />
      )}
      {navState.creatingSectionType === 'Related Documents' && (
        <RelatedDocumentsSlideoutWizard
          close={close}
          editingSection={
            navState.editingSection &&
            sectionTypeCheckers.isRelatedDocumentsSection(
              navState.editingSection,
            )
              ? navState.editingSection
              : undefined
          }
        />
      )}
      {navState.creatingSectionType === 'Related Metric Documents' && (
        <AllMetricsGate>
          <RelatedMetricDocumentsSlideoutWizard
            close={close}
            editingSection={
              navState.editingSection &&
              sectionTypeCheckers.isRelatedMetricDocumentsSection(
                navState.editingSection,
              )
                ? navState.editingSection
                : undefined
            }
          />
        </AllMetricsGate>
      )}
      {navState.creatingSectionType === 'Bonus Total Reward' && (
        <BonusTotalRewardWizard
          close={close}
          lockedBonusConfigId={lockedBonusConfigId}
          editingSection={
            navState.editingSection &&
            sectionTypeCheckers.isBonusTotalRewardSection(
              navState.editingSection,
            )
              ? navState.editingSection
              : undefined
          }
        />
      )}
      {navState.creatingSectionType === 'Bonus Reward' && (
        <BonusRewardWizard
          close={close}
          lockedBonusConfigId={lockedBonusConfigId}
          editingSection={
            navState.editingSection &&
            sectionTypeCheckers.isBonusRewardSection(navState.editingSection)
              ? navState.editingSection
              : undefined
          }
        />
      )}
      {navState.creatingSectionType === 'Bonus Eligibility' && (
        <BonusEligibilityWizard
          close={close}
          lockedBonusConfigId={lockedBonusConfigId}
          editingSection={
            navState.editingSection &&
            sectionTypeCheckers.isBonusEligibilitySection(
              navState.editingSection,
            )
              ? navState.editingSection
              : undefined
          }
        />
      )}
      {navState.creatingSectionType === 'Additional Payments' && (
        <RecognitionPaymentsWizard
          close={close}
          lockedBonusConfigId={lockedBonusConfigId}
          editingSection={
            navState.editingSection &&
            sectionTypeCheckers.isRecognitionPaymentsSection(
              navState.editingSection,
            )
              ? navState.editingSection
              : undefined
          }
        />
      )}
    </>
  );
};

export default WizardSlideout;
